/**
 * src/components/ui/BarraTramos.jsx
 * Un total repartido en cubos, dibujado en vez de contado.
 *
 * Nace de un hallazgo del 02-09. La ficha de `solofresco` escribía esto:
 *
 *     Vigilado $1.463.716.849 · en tiendas que no vigilamos $0 ·
 *     facturación propia total $1.772.615.013
 *
 * Tres importes en fila, con un «total» al final. Se leen como un reparto y no
 * lo son: **faltaban $209.727.479, un 11,8 %**, que ninguna línea explicaba.
 * Llevaba semanas en pantalla y nadie lo había visto, porque en prosa los
 * números no se suman con la vista. En una barra se ve el primer día.
 *
 * ⚠️⚠️ POR ESO ESTE COMPONENTE NO NORMALIZA. Lo natural al programar una barra
 * apilada es repartir el 100 % entre los tramos que te dan; eso habría tapado
 * el hueco para siempre y con mejor pinta que la prosa. Aquí, si los tramos no
 * llegan al total, aparece un tramo **«Sin explicar»** que nadie pidió. Es el
 * §5 —sin fallos mudos— aplicado a un dibujo: la barra delata a quien la usa mal.
 *
 * ⚠️ Y un tramo con `valor: null` NO es un tramo de cero: es un tramo que no se
 * ha medido. Se pinta rayado y se escribe «sin medir» (§3). Que «no lo sabemos»
 * y «no hay» compartan aspecto es exactamente el error más repetido del
 * proyecto. Un tramo con `valor: 0` de verdad medido no se pinta: no ocupa.
 *
 * El rayado se puede pedir también con `trama: true` sobre un tramo que SÍ
 * tiene importe. Significa «no sabemos dónde cae esto», que es el caso de los
 * convenios aún sin sondear: el dinero está contado, la tienda no. La leyenda
 * escribe la cifra igual, así que no hay ambigüedad entre los dos usos.
 */
import { clp, num, pct } from '../../lib/cifras'
import { Pista } from './Pista'

const _MINIMO_VISIBLE = 1.5   // % — un tramo con dinero nunca desaparece del todo

/**
 * Reparte el total entre los tramos y añade el que falte. Sin DOM: es la parte
 * con aritmética, y así se puede razonar sobre ella sin navegador.
 *
 * @param {number|null} total
 * @param {Array<{clave,etiqueta,valor,color?,trama?,pista?}>} tramos
 * @returns {{tramos: Array, hueco: number|null, medido: number}}
 */
export function repartir(total, tramos) {
  const lista = (tramos || []).filter(Boolean)
  // Los tramos sin medir NO suman: sumarlos como cero afirmaría que valen cero.
  const medido = lista.reduce((a, t) => a + (t.valor == null ? 0 : Number(t.valor)), 0)
  const hueco = (total == null) ? null : Math.round(Number(total) - medido)

  const salida = [...lista]
  // Un peso de diferencia es redondeo, no un hueco. Se avisa desde el 0,1 %.
  if (hueco != null && total && hueco / Number(total) > 0.001) {
    salida.push({
      clave: '__sin_explicar__',
      etiqueta: 'Sin explicar',
      valor: hueco,
      color: 'var(--color-error)',
      pista: 'Este trozo del total no lo cubre ninguno de los cubos de arriba. '
           + 'No es una categoría: es una cuenta que no cuadra, y está a la '
           + 'vista para que no se quede escondida.',
    })
  }
  return { tramos: salida, hueco, medido }
}

const _TRAMA = 'repeating-linear-gradient(135deg, var(--color-surface-2) 0 5px, var(--color-border) 5px 10px)'

/**
 * @param {number|null} total
 * @param {Array} tramos   {clave, etiqueta, valor, color?, trama?, pista?}
 *                         valor null = sin medir (rayado, nunca cero).
 * @param {'dinero'|'cuenta'} [unidad]
 * @param {string} [rotuloTotal]
 */
export function BarraTramos({ total, tramos, unidad = 'dinero', rotuloTotal }) {
  const fmt = unidad === 'dinero' ? clp : num
  const { tramos: lista } = repartir(total, tramos)
  const base = Number(total) || lista.reduce((a, t) => a + (Number(t.valor) || 0), 0)

  return (
    <div>
      {rotuloTotal && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)',
                      marginBottom: 'var(--space-2)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-lg)',
                         fontVariantNumeric: 'tabular-nums', color: 'var(--color-text)' }}>
            {fmt(total)}
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
            {rotuloTotal}
          </span>
        </div>
      )}

      {/* El dibujo no lleva texto alternativo ni `title`: la leyenda de abajo
          dice lo mismo, siempre visible y en texto de verdad. Duplicarlo aquí
          haría que un lector de pantalla lo cantara dos veces. */}
      <div data-barra="tramos" aria-hidden="true" style={{
        display: 'flex', height: '14px', width: '100%', gap: 'var(--space-05)',
        borderRadius: 'var(--radius-pill)', overflow: 'hidden',
        background: 'var(--color-surface-2)',
      }}>
        {lista.map(t => {
          const sinMedir = t.valor == null
          // Un tramo sin medir ocupa un hueco fijo y estrecho: no tiene tamaño
          // que representar, pero tiene que verse que está.
          const parte = sinMedir ? _MINIMO_VISIBLE
            : (base ? Math.max(_MINIMO_VISIBLE, (Number(t.valor) / base) * 100) : 0)
          if (!sinMedir && !Number(t.valor)) return null
          return (
            <div
              key={t.clave}
              data-tramo={t.clave}
              style={{
                width: `${parte}%`,
                background: (sinMedir || t.trama) ? _TRAMA : (t.color || 'var(--color-accent)'),
              }}
            />
          )
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1) var(--space-4)',
                    marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-3)', lineHeight: 1.6 }}>
        {lista.map(t => {
          const sinMedir = t.valor == null
          if (!sinMedir && !Number(t.valor)) return null
          const p = sinMedir ? null : pct(t.valor, base)
          return (
            <span key={t.clave} data-leyenda={t.clave}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <span aria-hidden="true" style={{
                width: '9px', height: '9px', borderRadius: 'var(--radius-pill)', flexShrink: 0,
                background: (sinMedir || t.trama) ? _TRAMA : (t.color || 'var(--color-accent)'),
              }} />
              <span style={{ color: 'var(--color-text-2)' }}>{t.etiqueta}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                {sinMedir ? 'sin medir' : fmt(t.valor)}{p != null && ` · ${p}%`}
              </span>
              {t.pista && <Pista contenido={t.pista} etiqueta={t.etiqueta} />}
            </span>
          )
        })}
      </div>
    </div>
  )
}
